import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useOrgStore } from '../store/useOrgStore';
import { StatCard } from '../components/ui/StatCard';
import { RiskBadge } from '../components/ui/RiskBadge';
import { EmptyState } from '../components/ui/EmptyState';
import { CollapsibleSection } from '../components/ui/CollapsibleSection';
import type { PermissionSetSummary, PermissionsResult } from '../types/org.types';

// Live audit store by default, which since TYR-216 is the client Hub alone;
// a stored client scan passes its own audit in.
// Passing `data` at all, null included, is scan mode: the store is not read.
export function PermissionsPage({ data }: { data?: PermissionsResult | null } = {}) {
  const storePermissions = useOrgStore((s) => s.audit.permissions);
  const scanMode = data !== undefined;
  const permissions = scanMode ? data : storePermissions;
  const [search, setSearch] = useState('');

  const psRows = useMemo(() => {
    if (!permissions) return [];
    return [...permissions.permissionSets]
      .filter((p) =>
        search
          ? p.name.toLowerCase().includes(search.toLowerCase()) ||
            p.label.toLowerCase().includes(search.toLowerCase())
          : true,
      )
      .sort((a, b) => b.assignmentCount - a.assignmentCount);
  }, [permissions, search]);

  // Standard permission sets nobody holds are Salesforce noise: they sit in
  // their own closed group so the list opens on what the org actually uses.
  const isUnusedStandard = (p: { isCustom: boolean; assignmentCount: number }) =>
    !p.isCustom && p.assignmentCount === 0;
  const usedRows = psRows.filter((p) => !isUnusedStandard(p));
  const unusedStandardRows = psRows.filter(isUnusedStandard);

  const profileRows = useMemo(() => {
    if (!permissions) return [];
    return [...permissions.profiles].sort((a, b) => b.userCount - a.userCount);
  }, [permissions]);

  if (!permissions) return <EmptyState title="No permission data. Reconnect to scan again." />;

  const t = permissions.totals;

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-widest text-adopt mb-1">Adopt</div>
        <h1 className="text-3xl font-semibold text-crux-light tracking-tight">Permissions</h1>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Custom PSLs" value={t.customPermissionSets} accent="adopt" />
        <StatCard label="PSL Assignments" value={t.permissionSetAssignments} accent="adopt" />
        <StatCard
          label="System Admins"
          value={t.systemAdminCount}
          accent={t.systemAdminCount > 5 ? 'sunset' : 'adopt'}
          hint={t.systemAdminCount > 5 ? 'May be excessive' : undefined}
        />
        <StatCard
          label="Custom Admin Profiles"
          value={t.customAdminProfiles}
          accent={t.customAdminProfiles > 2 ? 'sunset' : 'adopt'}
        />
      </div>

      <CollapsibleSection title="Profiles" meta={`${profileRows.length} total`}>
        <div className="border border-border rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-surface-2">
              <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
                <th className="text-left px-4 py-3 font-medium">Profile</th>
                <th className="text-left px-4 py-3 font-medium">Type</th>
                <th className="text-right px-4 py-3 font-medium">Users</th>
              </tr>
            </thead>
            <tbody>
              {profileRows.map((p, i) => (
                <tr key={p.name} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
                  <td className="px-4 py-2.5 text-crux-light">{p.name}</td>
                  <td className="px-4 py-2.5">
                    {p.isAdmin ? (
                      <RiskBadge tone="critical">Admin</RiskBadge>
                    ) : (
                      <RiskBadge tone="neutral">Standard</RiskBadge>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">
                    {p.userCount}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CollapsibleSection>

      <CollapsibleSection
        title="Permission Sets"
        meta={`${psRows.length} shown`}
        defaultOpen={false}
      >
        <div className="flex items-center justify-end mb-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-neutral-500" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search permission sets…"
              className="bg-surface-2 border border-border rounded-md pl-9 pr-3 py-1.5 text-sm text-crux-light placeholder:text-neutral-600 focus:outline-none focus:border-adopt w-64"
            />
          </div>
        </div>
        <PermissionSetTable rows={usedRows} />
        {unusedStandardRows.length > 0 ? (
          <details className="border border-border rounded-md mt-3">
            <summary className="cursor-pointer px-4 py-3 text-sm text-neutral-400 hover:text-crux-light">
              Standard permission sets with 0 assignments ({unusedStandardRows.length})
            </summary>
            <div className="p-3 pt-0">
              <PermissionSetTable rows={unusedStandardRows} />
            </div>
          </details>
        ) : null}
      </CollapsibleSection>
    </div>
  );
}

function PermissionSetTable({ rows }: { rows: PermissionSetSummary[] }) {
  return (
    <div className="border border-border rounded-md overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-surface-2">
          <tr className="text-[11px] uppercase tracking-widest text-neutral-500">
            <th className="text-left px-4 py-3 font-medium">Permission Set</th>
            <th className="text-left px-4 py-3 font-medium">Type</th>
            <th className="text-right px-4 py-3 font-medium">Direct Assignments</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((p, i) => (
            <tr key={p.id} className={i % 2 === 0 ? 'bg-surface-1/40' : 'bg-transparent'}>
              <td className="px-4 py-2.5">
                <div className="text-crux-light">{p.label}</div>
                <div className="text-[11px] text-neutral-600 font-mono">{p.name}</div>
              </td>
              <td className="px-4 py-2.5 text-neutral-400">{p.isCustom ? 'Custom' : 'Standard'}</td>
              <td className="px-4 py-2.5 text-right text-neutral-300 tabular-nums">{p.assignmentCount}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
